import React, { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import featureService from "../api/services/dashbaordService";
import { CardsSkeleton, PageSkeleton } from "../components/common/skeletons";
import userServices from "../api/services/userService";
import {
  DashboardUsersModal,
  DashboardOverviewCards,
  DashboardCharts,
  DashboardProposalsDetail,
  DashboardPaymentSection,
} from "../components/Dashboard";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

const MAIN_TABS = [
  { id: "overview", label: "Overview" },
  { id: "subscriptions", label: "Subscriptions" },
];

const THEME = { primary: "#FB565F" };

const Home = () => {
  const [mainTab, setMainTab] = useState("overview");
  const [dashboardData, setDashboardData] = useState(null);
  const [subscriptionsData, setSubscriptionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [showTodayUsersModal, setShowTodayUsersModal] = useState(false);
  const [showMonthUsersModal, setShowMonthUsersModal] = useState(false);
  const [todayUsers, setTodayUsers] = useState([]);
  const [totalToday, setTotalToday] = useState(0);
  const [monthUsers, setMonthUsers] = useState([]);
  const [totalMonth, setTotalMonth] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [currentPage, setCurrentPage] = useState(1);
  const [modalLoading, setModalLoading] = useState(false);
  const usersPerPage = 10;
  const hasFetchedOverview = useRef(false);

  useEffect(() => {
    document.body.style.overflowX = "hidden";
    return () => {
      document.body.style.overflowX = "";
    };
  }, []);

  useEffect(() => {
    if (showTodayUsersModal || showMonthUsersModal) {
      document.body.style.overflow = "hidden";
      document.body.style.overflowX = "hidden";
    } else {
      document.body.style.overflow = "";
      document.body.style.overflowX = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showTodayUsersModal, showMonthUsersModal]);

  // On load: fetch overview only once (ref guards against React Strict Mode double-invoke)
  useEffect(() => {
    if (hasFetchedOverview.current) return;
    hasFetchedOverview.current = true;
    setLoading(true);
    const fetchData = async () => {
      try {
        const result = await featureService.fetchOverview();
        if (result?.responseCode === 200) {
          setDashboardData(result);
        }
      } catch (error) {
        console.error("Error fetching dashboard overview:", error);
        hasFetchedOverview.current = false;
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // When user opens Subscriptions tab: fetch subscriptions data once
  useEffect(() => {
    if (mainTab !== "subscriptions" || subscriptionsData !== null || subscriptionsLoading) return;
    setSubscriptionsLoading(true);
    featureService
      .fetchDashboardSubscriptions()
      .then((result) => {
        if (result?.responseCode === 200) {
          setSubscriptionsData(result);
        }
      })
      .catch((err) => console.error("Error fetching subscriptions dashboard:", err))
      .finally(() => setSubscriptionsLoading(false));
  }, [mainTab, subscriptionsData, subscriptionsLoading]);

  const fetchTodayPage = async (page) => {
    setModalLoading(true);
    try {
      const result = await userServices.fetchAllUsers(page, null, "today");
      setTodayUsers(result?.users ?? []);
      setTotalToday(result?.total ?? 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching today users:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const fetchMonthPage = async (page, month = selectedMonth, year = selectedYear) => {
    setModalLoading(true);
    try {
      const result = await userServices.fetchAllUsers(page, null, "month", month, year);
      setMonthUsers(result?.users ?? []);
      setTotalMonth(result?.total ?? 0);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching month users:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const handleMonthYearChange = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    fetchMonthPage(1, month, year);
  };

  const handleTodayUsersClick = () => {
    setCurrentPage(1);
    setTodayUsers([]);
    setTotalToday(0);
    setShowTodayUsersModal(true);
    fetchTodayPage(1);
  };

  const handleMonthUsersClick = () => {
    const now = new Date();
    const m = now.getMonth() + 1;
    const y = now.getFullYear();
    setSelectedMonth(m);
    setSelectedYear(y);
    setCurrentPage(1);
    setMonthUsers([]);
    setTotalMonth(0);
    setShowMonthUsersModal(true);
    fetchMonthPage(1, m, y);
  };

  if (!dashboardData) {
    return (
      <div className="w-full min-h-[calc(100vh-120px)] overflow-x-hidden">
        <PageSkeleton showTabs showCards tableCols={6} tableRows={6} />
      </div>
    );
  }

  const {
    usersAnalytics,
    proposalsAnalytics,
    premiumCount,
    weekGenderDistribution,
    monthlyUserStats,
    subscriptionStats = null,
    subscriptionTrend = null,
  } = dashboardData;

  return (
    <>
      <div className="rounded-xl w-full min-h-[calc(100vh-120px)] overflow-x-hidden">

        <div className="p-4 md:p-6 lg:p-8">
          <div className="border-l-4 border-gray-900 pl-3 mb-6" style={{ borderColor: "#FB565F" }}>
            <h1 className="font-main text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">Overview of users, proposals, and subscriptions</p>
          </div>

          {/* Main tabs: Overview | Subscriptions */}
          <div className="flex flex-wrap gap-2 mb-6">
            {MAIN_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setMainTab(tab.id)}
                className={`font-main text-sm font-semibold px-5 py-2.5 rounded-xl transition-all ${mainTab === tab.id ? "text-white shadow-md" : "text-gray-600 hover:bg-gray-100 bg-white border border-gray-200"
                  }`}
                style={mainTab === tab.id ? { backgroundColor: THEME.primary } : {}}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {mainTab === "overview" && (
            <>
              {loading ? (
                <div className="space-y-5">
                  <CardsSkeleton count={4} />
                  <CardsSkeleton count={2} cols="grid-cols-1 sm:grid-cols-2" />
                </div>
              ) : (<>
                <DashboardOverviewCards
                  usersAnalytics={usersAnalytics}
                  proposalsAnalytics={proposalsAnalytics}
                  premiumCount={premiumCount}
                  subscriptionStats={subscriptionStats}
                  onTodayUsersClick={handleTodayUsersClick}
                  onMonthUsersClick={handleMonthUsersClick}
                />
                <DashboardProposalsDetail proposalsAnalytics={proposalsAnalytics} />
                <DashboardCharts
                  weekGenderDistribution={weekGenderDistribution}
                  monthlyUserStats={monthlyUserStats}
                  subscriptionTrend={subscriptionTrend}
                />
              </>)}
            </>
          )}

          {mainTab === "subscriptions" && (
            <>
              <DashboardPaymentSection
                loading={subscriptionsLoading}
                initialPaymentAnalytics={subscriptionsData?.paymentAnalytics}
                monthlyPaymentStats={subscriptionsData?.monthlyPaymentStats || []}
                monthlyPaymentStatsByCurrency={subscriptionsData?.monthlyPaymentStatsByCurrency || []}
              />
            </>
          )}

        </div>
      </div>

      <DashboardUsersModal
        title="Today Users"
        isOpen={showTodayUsersModal}
        onClose={() => setShowTodayUsersModal(false)}
        users={todayUsers}
        usersPerPage={usersPerPage}
        currentPage={currentPage}
        onPageChange={(page) => fetchTodayPage(page)}
        totalCount={totalToday}
        loading={modalLoading}
      />
      <DashboardUsersModal
        title="Monthly Users"
        isOpen={showMonthUsersModal}
        onClose={() => setShowMonthUsersModal(false)}
        users={monthUsers}
        usersPerPage={usersPerPage}
        currentPage={currentPage}
        onPageChange={(page) => fetchMonthPage(page, selectedMonth, selectedYear)}
        totalCount={totalMonth}
        loading={modalLoading}
        headerExtra={
          <div className="flex flex-wrap items-center gap-3 font-main">
            <label className="text-sm font-medium text-gray-700">Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => handleMonthYearChange(Number(e.target.value), selectedYear)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              aria-label="Select month">
              {MONTHS.map((name, i) => (
                <option key={name} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
            <label className="text-sm font-medium text-gray-700">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => handleMonthYearChange(selectedMonth, Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              aria-label="Select year">
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        }
      />
    </>
  );
};

export default Home;
